import type { BillingInterval, PlanChoice } from 'definitions/billing-plans';
import { BILLING_PLAN_CATALOGUE, BILLING_TRIAL_DAYS, planPeriodPrice } from 'definitions/billing-plans';
import { formatShortDate } from '@/state/onboarding/plan-model';
import { formatEuro } from './billing-format';

export type SummaryRow = { when: string; what: string; amount: string; emphasis?: boolean };

/** What choosing a plan would do, said before anything happens: the panel the person accepts. */
export type PlanSummaryModel = {
  title: string;
  line: string;
  rows: SummaryRow[];
  ctaLabel: string;
  note: string;
};

type PlanChangeDirection = 'upgrade' | 'downgrade';

export type DescribeParams = {
  /** Whose plan this is when it is not the viewer's own: a client an agency is adding or changing. */
  forName?: string | null;
  choice: PlanChoice;
  direction: PlanChangeDirection | null;
  hasPlan: boolean;
  isTrialing: boolean;
  trialAvailable: boolean;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  currentName: string | null;
  checkoutAvailable: boolean;
};

const intervalWord = (interval: BillingInterval) => (interval === 'year' ? 'year' : 'month');

/**
 * The wording follows the server's rules (prorationFor): during the trial any change is
 * immediate and free, an upgrade charges the difference now (prorated, so no figure is quoted
 * for it), and a downgrade waits for the period end. Amounts are the plan's own price.
 */
export function describeChoice(params: DescribeParams): PlanSummaryModel {
  const { choice, direction, hasPlan, isTrialing, trialAvailable, trialEnd, currentPeriodEnd, currentName, forName } =
    params;
  const name = BILLING_PLAN_CATALOGUE[choice.plan].name;
  const price = planPeriodPrice(choice);
  const priceText = `${formatEuro(price)} a ${intervalWord(choice.interval)}, excl. VAT`;
  const renews = currentPeriodEnd ? formatShortDate(new Date(currentPeriodEnd)) : 'Next period';

  if (!hasPlan) {
    return {
      title: trialAvailable ? `${name}, free for ${BILLING_TRIAL_DAYS} days.` : `${name}.`,
      line: `${priceText}.${forName ? ` For ${forName}.` : ''}`,
      rows: trialAvailable
        ? [
            { when: 'Today', what: 'Full access, install the 0.9 KB tracker', amount: '€0', emphasis: true },
            { when: 'After the trial', what: 'First charge, unless you cancel', amount: formatEuro(price) },
          ]
        : [{ when: 'Today', what: 'First charge', amount: formatEuro(price), emphasis: true }],
      ctaLabel: trialAvailable ? `Start ${BILLING_TRIAL_DAYS}-day free trial` : 'Continue to checkout',
      note: params.checkoutAvailable
        ? "You'll add a card on the next step. You can cancel anytime from Settings."
        : 'Card payments are not available yet. Please check back soon.',
    };
  }

  if (isTrialing) {
    return {
      title: `Switch to ${name}.`,
      line: `${priceText}. It applies now and your free trial continues.`,
      rows: [
        { when: 'Today', what: `Switch from ${currentName} to ${name}`, amount: '€0', emphasis: true },
        {
          when: trialEnd ? formatShortDate(new Date(trialEnd)) : 'After the trial',
          what: 'First charge, unless you cancel',
          amount: formatEuro(price),
        },
      ],
      ctaLabel: `Switch to ${name}`,
      note: 'Nothing is charged during the trial.',
    };
  }

  if (direction === 'upgrade') {
    return {
      title: `Upgrade to ${name}.`,
      line: `${priceText}. It applies now.`,
      rows: [
        {
          when: 'Today',
          what: `Move from ${currentName} to ${name}, charging the difference for the rest of this period`,
          amount: 'Prorated',
          emphasis: true,
        },
        { when: renews, what: `Renews as ${name}`, amount: formatEuro(price) },
      ],
      ctaLabel: `Upgrade to ${name}`,
      note: 'Your card on file is charged today. You can change plan again at any time.',
    };
  }

  return {
    title: `Switch to ${name}.`,
    line: `${priceText}. It starts at the end of this period.`,
    rows: [
      { when: 'Today', what: `You keep ${currentName} until the period ends`, amount: '€0', emphasis: true },
      { when: renews, what: `${name} starts`, amount: formatEuro(price) },
    ],
    ctaLabel: `Switch to ${name} next period`,
    note: 'Nothing is refunded for the time left on the current plan.',
  };
}
